import React, { useState, useEffect, useRef } from 'react';
import { Branch, Category, ProductCreateInput, BranchProductAssignInput } from '../../../types';
import api from '../../../api/client';

interface ProductFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (productData: FormData, assignData: BranchProductAssignInput) => Promise<void>;
  categories: Category[];
  branches: Branch[];
  isLoading: boolean;
  initialData?: Partial<ProductCreateInput>;
  initialBranchData?: {
    branchId: number;
    branchPrice: number;
    stock: number;
    isAvailable: boolean;
  };
  initialImageUrl?: string;
  productId?: number; // For SKU validation exclusion during editing
}

interface FormState extends ProductCreateInput {
  category_ids: number[];
  is_featured: boolean;
  badge: string;
}

interface BranchFormState {
  branchId: number | null;
  branchPrice: number;
  stock: number;
  isAvailable: boolean;
}

const ProductFormModal: React.FC<ProductFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  categories,
  branches,
  isLoading,
  initialData,
  initialBranchData,
  initialImageUrl,
  productId,
}) => {
  const [formState, setFormState] = useState<FormState>({
    name: '',
    sku: '',
    description: '',
    short_description: '',
    base_price: 0,
    category_ids: [],
    is_featured: false,
    badge: '',
  });
  const [branchState, setBranchState] = useState<BranchFormState>({
    branchId: null,
    branchPrice: 0,
    stock: 0,
    isAvailable: true,
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [skuError, setSkuError] = useState<string | null>(null);
  const [isValidatingSku, setIsValidatingSku] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);

  // Initialize form with initialData and initialBranchData
  useEffect(() => {
    if (isOpen) {
      setFormState({
        name: initialData?.name || '',
        sku: initialData?.sku || '',
        description: initialData?.description || '',
        short_description: initialData?.short_description || '',
        base_price: initialData?.base_price || 0,
        category_ids: initialData?.category_ids || [],
        is_featured: initialData?.is_featured ?? false,
        badge: initialData?.badge || '',
      });
      setBranchState({
        branchId: initialBranchData?.branchId || null,
        branchPrice: initialBranchData?.branchPrice || 0,
        stock: initialBranchData?.stock || 0,
        isAvailable: initialBranchData?.isAvailable ?? true,
      });
      setImagePreview(initialImageUrl || null);
      setImageFile(null);
      setFormError(null);
      setSkuError(null);
    }
  }, [isOpen, initialData, initialBranchData, initialImageUrl]);

  // Focus first input when modal opens
  useEffect(() => {
    if (isOpen && firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, [isOpen]);

  // Clean up image preview URL
  useEffect(() => {
    return () => {
      if (imagePreview && !initialImageUrl) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview, initialImageUrl]);

  // Validate SKU with debounce
  useEffect(() => {
    if (formState.sku) {
      const validateSku = async () => {
        setIsValidatingSku(true);
        try {
          const params: { sku: string; exclude_product_id?: number } = { sku: formState.sku };
          if (productId) {
            params.exclude_product_id = productId;
          }
          const response = await api.get<{ available: boolean }>('/products/check-sku/', { params });
          setSkuError(response.data.available ? null : 'SKU is already in use');
        } catch (err) {
          setSkuError('Failed to validate SKU');
        } finally {
          setIsValidatingSku(false);
        }
      };
      const debounce = setTimeout(validateSku, 500);
      return () => clearTimeout(debounce);
    } else {
      setSkuError(null);
    }
  }, [formState.sku, productId]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      if (name === 'is_available') {
        setBranchState((prev) => ({ ...prev, isAvailable: checked }));
      } else {
        setFormState((prev) => ({ ...prev, [name]: checked }));
      }
    } else if (name === 'branchId') {
      setBranchState((prev) => ({ ...prev, branchId: value ? parseInt(value) : null }));
    } else if (name === 'branchPrice') {
      setBranchState((prev) => ({ ...prev, branchPrice: parseFloat(value) || 0 }));
    } else if (name === 'stock') {
      setBranchState((prev) => ({ ...prev, stock: parseInt(value) || 0 }));
    } else if (name === 'base_price') {
      setFormState((prev) => ({ ...prev, base_price: parseFloat(value) || 0 }));
    } else {
      setFormState((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOptions = Array.from(e.target.selectedOptions).map((option) =>
      parseInt(option.value)
    );
    setFormState((prev) => ({ ...prev, category_ids: selectedOptions }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      if (imagePreview && !initialImageUrl) {
        URL.revokeObjectURL(imagePreview);
      }
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    if (imagePreview && !initialImageUrl) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!branchState.branchId) {
      setFormError('Please select a branch');
      return;
    }
    if (!formState.name || !formState.sku || formState.base_price <= 0) {
      setFormError('Please fill in all required fields with valid values');
      return;
    }
    if (branchState.branchPrice <= 0 || branchState.stock < 0) {
      setFormError('Branch price must be greater than 0 and stock cannot be negative');
      return;
    }
    if (imageFile && !['image/jpeg', 'image/png'].includes(imageFile.type)) {
      setFormError('Please upload a JPEG or PNG image');
      return;
    }
    if (imageFile && imageFile.size > 5 * 1024 * 1024) {
      setFormError('Image size must be less than 5MB');
      return;
    }
    if (skuError) {
      setFormError('Please resolve SKU validation errors');
      return;
    }

    try {
      const productData = new FormData();
      productData.append('name', formState.name);
      productData.append('sku', formState.sku);
      productData.append('description', formState.description);
      productData.append('short_description', formState.short_description);
      productData.append('base_price', formState.base_price.toString());
      if (formState.category_ids.length > 0) {
        productData.append('category_ids', JSON.stringify(formState.category_ids));
      }
      productData.append('is_featured', formState.is_featured.toString());
      productData.append('badge', formState.badge);
      if (imageFile) {
        productData.append('image', imageFile);
      } else if (!imagePreview && initialImageUrl) {
        productData.append('image', ''); // Signal to remove image
      }

      const assignData: BranchProductAssignInput = {
        product_id: 0, // Will be set after product creation
        branch_id: branchState.branchId,
        price: branchState.branchPrice,
        stock: branchState.stock,
        is_available: branchState.isAvailable,
      };

      await onSubmit(productData, assignData);
      onClose();
    } catch (err) {
      setFormError('Failed to save product');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && !isLoading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="product-modal-title"
    >
      <div
        ref={modalRef}
        className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto"
      >
        <h3 id="product-modal-title" className="text-xl font-semibold mb-4">
          {initialData ? 'Edit Product' : 'Add New Product'}
        </h3>
        {formError && (
          <div className="text-red-600 bg-red-50 p-2 rounded mb-4" role="alert">
            {formError}
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              Name *
            </label>
            <input
              id="name"
              type="text"
              name="name"
              value={formState.name}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary focus:border-primary"
              required
              ref={firstInputRef}
              aria-required="true"
            />
          </div>
          <div>
            <label htmlFor="sku" className="block text-sm font-medium text-gray-700">
              SKU *
            </label>
            <div className="relative">
              <input
                id="sku"
                type="text"
                name="sku"
                value={formState.sku}
                onChange={handleInputChange}
                className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-primary focus:border-primary ${skuError ? 'border-red-500' : 'border-gray-300'}`}
                required
                aria-required="true"
                aria-describedby={skuError ? 'sku-error' : undefined}
              />
              {isValidatingSku && (
                <span className="absolute right-2 top-2 text-gray-500 text-sm">Validating...</span>
              )}
            </div>
            {skuError && (
              <p id="sku-error" className="text-red-600 text-sm mt-1" role="alert">
                {skuError}
              </p>
            )}
          </div>
          <div>
            <label htmlFor="short_description" className="block text-sm font-medium text-gray-700">
              Short Description
            </label>
            <textarea
              id="short_description"
              name="short_description"
              value={formState.short_description}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary focus:border-primary"
              rows={3}
            />
          </div>
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formState.description}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary focus:border-primary"
              rows={5}
            />
          </div>
          <div>
            <label htmlFor="base_price" className="block text-sm font-medium text-gray-700">
              Base Price *
            </label>
            <input
              id="base_price"
              type="number"
              name="base_price"
              value={formState.base_price}
              onChange={handleInputChange}
              min="0"
              step="0.01"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary focus:border-primary"
              required
              aria-required="true"
            />
          </div>
          <div>
            <label htmlFor="image" className="block text-sm font-medium text-gray-700">
              Image
            </label>
            <input
              id="image"
              type="file"
              accept="image/jpeg,image/png"
              onChange={handleImageChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              aria-describedby="image-help"
              disabled={isLoading}
            />
            <p id="image-help" className="text-sm text-gray-500 mt-1">
              Upload a JPEG or PNG image (max 5MB)
            </p>
            {imagePreview && (
              <div className="mt-2">
                <img
                  src={imagePreview}
                  alt="Image Preview"
                  className="w-32 h-32 object-contain rounded"
                />
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="mt-2 text-red-600 hover:underline text-sm"
                  aria-label="Remove image"
                  disabled={isLoading}
                >
                  Remove Image
                </button>
              </div>
            )}
          </div>
          <div>
            <label htmlFor="category_ids" className="block text-sm font-medium text-gray-700">
              Categories
            </label>
            <select
              id="category_ids"
              multiple
              name="category_ids"
              value={formState.category_ids.map(String)}
              onChange={handleCategoryChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary focus:border-primary"
              aria-multiselectable="true"
              disabled={isLoading}
            >
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="flex items-center">
              <input
                id="is_featured"
                type="checkbox"
                name="is_featured"
                checked={formState.is_featured}
                onChange={handleInputChange}
                className="mr-2"
                disabled={isLoading}
              />
              <span className="text-sm font-medium text-gray-700">Featured Product</span>
            </label>
          </div>
          <div>
            <label htmlFor="badge" className="block text-sm font-medium text-gray-700">
              Badge
            </label>
            <input
              id="badge"
              type="text"
              name="badge"
              value={formState.badge}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary focus:border-primary"
              disabled={isLoading}
            />
          </div>
          <div>
            <label htmlFor="branchId" className="block text-sm font-medium text-gray-700">
              Branch *
            </label>
            <select
              id="branchId"
              name="branchId"
              value={branchState.branchId || ''}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary focus:border-primary"
              required
              aria-required="true"
              disabled={isLoading}
            >
              <option value="">Select a branch</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="branchPrice" className="block text-sm font-medium text-gray-700">
              Branch Price *
            </label>
            <input
              id="branchPrice"
              type="number"
              name="branchPrice"
              value={branchState.branchPrice}
              onChange={handleInputChange}
              min="0"
              step="0.01"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary focus:border-primary"
              required
              aria-required="true"
              disabled={isLoading}
            />
          </div>
          <div>
            <label htmlFor="stock" className="block text-sm font-medium text-gray-700">
              Stock *
            </label>
            <input
              id="stock"
              type="number"
              name="stock"
              value={branchState.stock}
              onChange={handleInputChange}
              min="0"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary focus:border-primary"
              required
              aria-required="true"
              disabled={isLoading}
            />
          </div>
          <div>
            <label className="flex items-center">
              <input
                id="is_available"
                type="checkbox"
                name="is_available"
                checked={branchState.isAvailable}
                onChange={handleInputChange}
                className="mr-2"
                disabled={isLoading}
              />
              <span className="text-sm font-medium text-gray-700">Available in Branch</span>
            </label>
          </div>
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500"
              disabled={isLoading}
              aria-label="Cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
              disabled={isLoading || isValidatingSku}
              aria-label={initialData ? 'Update product' : 'Create product'}
            >
              {isLoading ? 'Saving...' : initialData ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductFormModal;